import RootState from 'state/RootState';
import { AddFrameCommand } from './AddFrameCommand';
import { CommandFunction } from './Command';
import { PointData } from 'data/FlyerData';
import { getBackgroundStyleFromImageFile, loadImage } from 'utils';
import { createPngFileFromCanvas } from 'utils/GraphicsUtils';
import { getLayerById } from 'state/FlyerState';
import { PictureFrame } from 'data/PictureFrame';
import { reportIssue } from 'utils/Tracker';
import { FrameType } from 'data/FrameType';
import BackgroundStyle from 'data/BackgroundStyle';

export const resizeImageFileIfNecessary = async (file: File): Promise<File> => {
  const url = URL.createObjectURL(file);
  const img = await loadImage(url);
  const maxSize = 1920;
  if (img.width > 3000 && img.height > 3000) {
    const smallestSide = Math.min(img.width, img.height);
    const scale = maxSize / smallestSide;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);
    alert(
      `Because your image was very large (${img.width}x${img.height}), it has been resized (${canvas.width}x${canvas.height}).`
    );
    context?.drawImage(img, 0, 0, canvas.width, canvas.height);
    const parts = file.name.split('.');
    parts.pop();
    parts.push('png');
    file = await createPngFileFromCanvas(canvas, parts.join('.'));
  }
  URL.revokeObjectURL(url);
  return file;
};

export class AddPictureFrameCommand extends AddFrameCommand {
  file: File;
  scaleUp: boolean;
  fitSize: PointData | null;
  selectFrame: boolean;
  constructor(
    layerId: string | undefined,
    file: File,
    scaleUp: boolean,
    fitSize: PointData | null,
    selectFrame: boolean
  ) {
    super(layerId);
    this.file = file;
    this.scaleUp = scaleUp;
    this.fitSize = fitSize;
    this.selectFrame = selectFrame;
  }
  execute(firstTime?: boolean): CommandFunction {
    return async (dispatch: Function, getState: () => RootState) => {
      const { app, flyer } = getState();
      const { resolution } = flyer;
      const layer = getLayerById(flyer, this.layerId);
      const frame = new PictureFrame(resolution.width, resolution.height);
      const f = this.file;
      const fileType = f.type.split('/')[0];
      const format = f.type.split('/')[1];
      frame.type =
        fileType === 'video'
          ? FrameType.Video
          : format === 'gif'
          ? FrameType.AnimatedGIF
          : FrameType.Image;
      let file = f;
      if (frame.type === FrameType.Image) {
        // check size
        file = await resizeImageFileIfNecessary(file);
      }
      frame.file = file;
      frame.filename = file.name;
      frame.backgroundStyle =
        frame.type === FrameType.Video
          ? BackgroundStyle.Opaque
          : await getBackgroundStyleFromImageFile(file);
      try {
        await frame.load();
      } catch (e) {
        alert(`Could not add graphics because of error:\n\n${e}`);
        reportIssue(e);
        return;
      }

      const { width, height } = frame.boundingBox;
      const { scaleUp, fitSize } = this;
      if (scaleUp) {
        if (width < resolution.width || height < resolution.height) {
          // fill canvas
          const scaleX = resolution.width / width;
          const scaleY = resolution.height / height;
          const scale = Math.max(scaleX, scaleY);
          const newWidth = width * scale;
          const newHeight = height * scale;
          frame.boundingBox = new PIXI.Rectangle(
            (resolution.width - newWidth) / 2,
            (resolution.height - newHeight) / 2,
            newWidth,
            newHeight
          );
        }
      } else if (fitSize) {
        if (width > fitSize.x || height > fitSize.y) {
          const scaleX = fitSize.x / width;
          const scaleY = fitSize.y / height;
          const scale = Math.min(scaleX, scaleY);
          const newWidth = width * scale;
          const newHeight = height * scale;
          frame.boundingBox = new PIXI.Rectangle(
            (resolution.width - newWidth) / 2,
            (resolution.height - newHeight) / 2,
            newWidth,
            newHeight
          );
        }
      }
      this.dispatchWithFrame(
        dispatch,
        frame,
        layer,
        app.openedModals.length > 0,
        firstTime && this.selectFrame
      );
    };
  }
}



// WEBPACK FOOTER //
// ./src/state/commands/AddPictureFrameCommand.ts